/**
 * Recipe Photo Photography Styles & Prompt Builder for Admin CRM
 * Defines authentic home-cooking photography styles strictly modeled after Mealime
 * for Gemini image generation, with random selection by default and explicit override support.
 */

export const RECIPE_PHOTO_STYLES = [
  {
    id: "mealime_striped_linen_fork",
    name: "Striped Linen & Fork (Mealime Classic)",
    tableware: "Served in a wide shallow speckled ceramic dinner bowl with generous portions.",
    cameraAngle: "Shot from a 45-degree elevated angle looking directly into the wide shallow dinner bowl.",
    framing: "Tight medium-close framing where the shallow bowl fills 85-90% of the entire frame, with the food as the prominent hero and minimal surrounding background.",
    surfaceAndProps: "Set on a bright, clean, modern kitchen countertop with pale white quartz or light marble, with a soft white linen napkin with thin subtle stripes underneath and a clean stainless fork resting nearby. A small glass jar with fresh herbs softly blurred in the background.",
    lighting: "Bright, airy, clean daylight with natural white balance, soft shadows, and fresh vibrant colors."
  },
  {
    id: "mealime_slate_linen_prep_bowl",
    name: "Slate Linen & Prep Bowl (Mealime Fresh)",
    tableware: "Served in a wide shallow speckled ceramic dinner bowl with generous home portions.",
    cameraAngle: "Shot from a 45-degree elevated angle looking directly into the wide shallow dinner bowl.",
    framing: "Tight medium-close framing where the shallow bowl fills 85-90% of the entire frame, with the food as the prominent hero and minimal surrounding background.",
    surfaceAndProps: "Set on a bright, clean, modern kitchen countertop with pale light marble, with a corner of a soft slate-blue or muted grey washed linen cloth underneath, and a tiny clear glass prep bowl with chopped green scallions or fresh herbs in the upper corner.",
    lighting: "Bright, airy, clean daylight with natural white balance, soft shadows, and fresh vibrant colors."
  },
  {
    id: "mealime_rimmed_plate_lemon",
    name: "Rimmed Plate with Black Rim (Mealime Plated)",
    tableware: "Served on a wide shallow off-white ceramic rimmed dinner plate with a subtle black line that goes around the tip of the rim, with generous portions.",
    cameraAngle: "Shot from a 45-degree elevated angle looking directly into the wide shallow rimmed dinner plate.",
    framing: "Tight medium-close framing where the wide off-white ceramic rimmed plate fills 85-90% of the entire frame, with the food as the prominent hero and minimal surrounding background.",
    surfaceAndProps: "Set on a bright, clean, modern kitchen countertop with pale white quartz, with a corner of a soft light linen napkin underneath, and a clean fork resting on the side.",
    lighting: "Bright, airy, clean daylight with natural white balance, soft shadows, and fresh vibrant colors."
  },
  {
    id: "mealime_matte_bowl_herb_garnish",
    name: "Matte Oatmeal Bowl (Mealime Cozy)",
    tableware: "Served in a modern matte ceramic dinner bowl in soft dove-gray or pale oatmeal glaze with generous portions.",
    cameraAngle: "Shot from a 45-degree elevated angle looking directly into the wide shallow dinner bowl.",
    framing: "Tight medium-close framing where the shallow ceramic bowl fills 85-90% of the entire frame, with the food as the prominent hero and minimal surrounding background.",
    surfaceAndProps: "Set on a bright, clean, modern kitchen countertop with pale white quartz, with a textured light neutral kitchen cloth folded beside the bowl and a clean stainless spoon or fork.",
    lighting: "Bright, airy, clean daylight with natural white balance, soft shadows, and fresh vibrant colors."
  },
  {
    id: "mealime_minimalist_quartz",
    name: "Minimalist Quartz Counter (Mealime Clean)",
    tableware: "Served in a wide shallow speckled ceramic dinner bowl with generous portions.",
    cameraAngle: "Shot from a 45-degree elevated angle looking directly into the wide shallow dinner bowl.",
    framing: "Tight medium-close framing where the shallow bowl fills 85-90% of the entire frame, with the food as the prominent hero and minimal surrounding background.",
    surfaceAndProps: "Set directly on a bright, clean, modern kitchen countertop with light white marble or pale quartz surface. Minimalist, uncluttered, airy presentation without cutlery.",
    lighting: "Bright, airy, clean daylight with natural white balance, soft shadows, and fresh vibrant colors."
  }
];

/**
 * Returns a photo style by ID, or a randomly chosen style if none specified or 'random'.
 * @param {string|null} preferredStyleId
 * @returns {object} The chosen style object
 */
export function getRandomPhotoStyle(preferredStyleId = null) {
  if (preferredStyleId && preferredStyleId !== "random") {
    const matched = RECIPE_PHOTO_STYLES.find(
      (s) => s.id === preferredStyleId || s.name.toLowerCase() === preferredStyleId.toLowerCase()
    );
    if (matched) return matched;
  }
  const randomIndex = Math.floor(Math.random() * RECIPE_PHOTO_STYLES.length);
  return RECIPE_PHOTO_STYLES[randomIndex];
}

/**
 * Builds an authentic home-cooked recipe photo prompt for Gemini image generation
 * returning both the prompt text and the selected style object.
 *
 * @param {string} title
 * @param {Array|string} ingredients
 * @param {Array|string} instructions
 * @param {object} options
 * @returns {{ promptText: string, selectedStyle: object }}
 */
export function buildRecipePhotoPromptWithMetadata(title, ingredients = [], instructions = [], options = {}) {
  const style = getRandomPhotoStyle(options.style || options.photo_style || options.photoStyle);

  const ingText = Array.isArray(ingredients)
    ? ingredients
        .map((i) =>
          typeof i === "string"
            ? i
            : `${i.amount != null ? i.amount : ""} ${i.unit || ""} ${
                i.name_singular || i.name || i.display_name || i.ingredient_id || i.uid || ""
              }`.trim()
        )
        .filter(Boolean)
        .join(", ")
    : String(ingredients || "");

  const instrText = Array.isArray(instructions)
    ? instructions
        .map((s, idx) => `${idx + 1}. ${typeof s === "string" ? s : s.text || s.instruction || s.step || ""}`)
        .join("\n")
    : String(instructions || "");

  const promptText = `An authentic, appetizing, modern home-cooked meal: "${title}".
Style inspired by clean modern recipe apps like Mealime:
Real authentic food photography of a real weeknight home-cooked dish, shot on an iPhone with soft natural window light.
${style.cameraAngle}
${style.framing}
${style.tableware}
${style.surfaceAndProps}
${style.lighting}
Natural, realistic home-cooked food textures: honest cooking textures, tender ingredients, melted cheese, golden edges, and fresh chopped herbs.
NOT 3D CGI, NOT digital illustration, NOT commercial studio food advertising, NOT hyper-glossy or plastic, NOT dramatic dark lighting, NOT a dark rustic wooden table, NO dining room furniture, NO tweezer plating.

Recipe Details:
Ingredients: ${ingText || "Fresh pantry ingredients"}
Cooking Summary:
${instrText || "Cooked home-style on the stovetop or oven"}`;

  return {
    promptText,
    selectedStyle: style
  };
}

/**
 * Builds an authentic home-cooked recipe photo prompt for Gemini image generation.
 */
export function buildRecipePhotoPrompt(title, ingredients = [], instructions = [], options = {}) {
  return buildRecipePhotoPromptWithMetadata(title, ingredients, instructions, options).promptText;
}
